import { z } from 'zod';
import { FILE_KINDS, FILE_PURPOSES, MAX_UPLOAD_BYTES } from '../domain';

export const filePurposeSchema = z.enum(FILE_PURPOSES);

export const createUploadInput = z.object({
  name: z.string().trim().min(1).max(255),
  sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES, 'Arquivo maior que 20 GB'),
  mimeType: z.string().trim().max(150).default('application/octet-stream'),
  purpose: filePurposeSchema,
  clientId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  taskId: z.uuid().optional(),
  requestId: z.uuid().optional(),
  boardId: z.uuid().optional(),
  storyboardId: z.uuid().optional(),
  folderId: z.uuid().nullable().optional(),
  visibility: z.enum(['internal', 'client']).default('internal'),
});
export type CreateUploadInput = z.input<typeof createUploadInput>;

export const createdUploadDto = z.object({
  fileId: z.uuid(),
  versionId: z.uuid(),
  /** 'single': um PUT em uploadUrl. 'multipart': pedir URLs das partes em /files/:id/parts. */
  mode: z.enum(['single', 'multipart']),
  partSize: z.number().optional(),
  partCount: z.number().optional(),
  uploadUrl: z.string(),
  /** Cabeçalhos que o PUT precisa enviar exatamente assim (fazem parte da assinatura). */
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type CreatedUploadDto = z.infer<typeof createdUploadDto>;

export const fileDto = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(FILE_KINDS),
  purpose: filePurposeSchema,
  visibility: z.enum(['internal', 'client']),
  mimeType: z.string(),
  sizeBytes: z.number(),
  version: z.number(),
  /** URL estável do app (/api/files/:id/content) que redireciona para uma URL assinada. */
  url: z.string(),
  thumbnailUrl: z.string().nullable(),
  folderId: z.uuid().nullable(),
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type FileDto = z.infer<typeof fileDto>;

export const fileListQuery = z.object({
  /** Pasta da biblioteca ('root' = raiz do escopo). */
  folderId: z.union([z.uuid(), z.literal('root')]).optional(),
  q: z.string().trim().max(120).optional(),
  kind: z.string().max(20).optional(),
  clientId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  taskId: z.uuid().optional(),
  requestId: z.uuid().optional(),
  purpose: filePurposeSchema.optional(),
});

export const updateFileInput = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  visibility: z.enum(['internal', 'client']).optional(),
});

export const updateFileMetaInput = z.object({ folderId: z.uuid().nullable().optional() });

export const partsInput = z.object({
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(50),
});
export const completeUploadInput = z.object({
  versionId: z.uuid().optional(),
  parts: z
    .array(z.object({ partNumber: z.number().int().min(1), etag: z.string().min(1).max(200) }))
    .max(10_000)
    .optional(),
});

export const newVersionInput = z.object({
  name: z.string().trim().min(1).max(255),
  sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES, 'Arquivo maior que 20 GB'),
  mimeType: z.string().trim().max(150).default('application/octet-stream'),
  comment: z.string().trim().max(1000).optional(),
});

export const fileVersionDto = z.object({
  id: z.uuid(),
  versionNumber: z.number(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  comment: z.string().nullable(),
  uploadedBy: z.string().nullable(),
  uploadedAt: z.string().nullable(),
  current: z.boolean(),
  url: z.string(),
  /** Aprovação da versão (Fase 14). */
  status: z.string(),
});
export type FileVersionDto = z.infer<typeof fileVersionDto>;

export const folderDto = z.object({
  id: z.uuid(),
  name: z.string(),
  parentId: z.uuid().nullable(),
  clientId: z.uuid().nullable(),
  projectId: z.uuid().nullable(),
  fileCount: z.number(),
});
export type FolderDto = z.infer<typeof folderDto>;
export const folderInput = z.object({
  name: z.string().trim().min(1).max(80),
  parentId: z.uuid().nullable().optional(),
  clientId: z.uuid().nullable().optional(),
  projectId: z.uuid().nullable().optional(),
});
export const folderQuery = z.object({
  clientId: z.uuid().optional(),
  projectId: z.uuid().optional(),
  scope: z.enum(['studio', 'client', 'project']),
});
